import type {
  MarginModifierValue,
  PaddingModifierValue,
} from "schemas/generated/fileModifiers";

import React, { useCallback, useMemo, useState } from "react";

import DirectionBottom from "@svg/modifiers/direction/direction-bottom";
import DirectionHorizontal from "@svg/modifiers/direction/direction-horizontal";
import DirectionLeft from "@svg/modifiers/direction/direction-left";
import DirectionRight from "@svg/modifiers/direction/direction-right";
import DirectionTop from "@svg/modifiers/direction/direction-top";
import DirectionVertical from "@svg/modifiers/direction/direction-vertical";
import ExpandModifierOptions from "@svg/modifiers/expand-modifier-options";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import { parsePixelValue } from "@agent-ui/features/modifiers/utils/parseComputedValue";
import { Button } from "@replo/design-system/components/button/Button";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import {
  MARGIN_MODIFIER_OPTIONS,
  PADDING_MODIFIER_OPTIONS,
} from "@v2/features/modifiers/constants";
import { ChevronDown } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import {
  parseCommaSeparatedValues,
  parseUnit,
} from "replo-runtime/shared/utils/units";

type SpacingMode = "unified" | "individual";
type SpacingType = "margin" | "padding";
type SpacingModifierType = MarginModifierValue | PaddingModifierValue;
type MarginValue = number | "auto";

interface SpacingValue {
  top?: MarginValue;
  right?: MarginValue;
  bottom?: MarginValue;
  left?: MarginValue;
}

interface SpacingField {
  key: string;
  label: string;
  value: MarginValue | undefined;
  secondaryValue?: MarginValue | undefined;
  onChange: (newValue: MarginValue | undefined) => void;
  onChangeMixed?: (
    primaryValue: MarginValue | undefined,
    secondaryValue: MarginValue | undefined,
  ) => void;
  icon?: React.ReactNode;
}

interface SpacingModifierConfig {
  type: SpacingType;
  unifiedTooltipText: string;
  individualTooltipText: string;
}

interface SpacingModifierProps<T extends SpacingModifierType> {
  onChange: (modifiers: T[]) => void;
  config: SpacingModifierConfig;
}

function getCommonLengthInputProps(spacingType: SpacingType) {
  const options =
    spacingType === "margin"
      ? MARGIN_MODIFIER_OPTIONS
      : PADDING_MODIFIER_OPTIONS;
  const allowedNonUnitValues = spacingType === "margin" ? ["auto"] : [];
  const minValues = spacingType === "padding" ? { px: 0 } : undefined;

  return {
    placeholder: "0px",
    metrics: ["px"],
    unitDefaults: { value: "0", unit: "px" },
    menuOptions: options.map((option) => ({
      ...option,
      value: option.value.toString(),
    })),
    minValues,
    inputClassName: "w-full",
    endEnhancer: () => <ChevronDown size={12} />,
    allowedNonUnitValues,
  };
}

function clampForPadding(value: number, spacingType: SpacingType): number {
  return spacingType === "padding" && value < 0 ? 0 : value;
}

function parseSpacingInputValue(
  newValue: string,
  spacingType: SpacingType,
): MarginValue | undefined {
  const allowedNonUnitValues = spacingType === "margin" ? ["auto"] : [];
  const result = parseUnit(
    newValue,
    { value: 0, unit: "px" },
    "default",
    "px",
    undefined,
    undefined,
    allowedNonUnitValues,
  );

  if (result.value === "auto") {
    return "auto";
  }

  const numericValue =
    typeof result.value === "number"
      ? result.value
      : Number.parseFloat(result.value);
  return clampForPadding(numericValue, spacingType);
}

function formatMarginValue(
  value: MarginValue | undefined,
  options: { includeUnit?: boolean; fallback?: string } = {},
): string {
  const { includeUnit = true, fallback = "" } = options;
  if (value === "auto") {
    return "Auto";
  }
  if (value != null) {
    return includeUnit ? `${value}px` : `${value}`;
  }
  return fallback;
}

interface SpacingInputProps {
  field: SpacingField;
  spacingType: SpacingType;
}

const SpacingInput: React.FC<SpacingInputProps> = ({ field, spacingType }) => {
  const primaryValue = field.value;
  const secondaryValue = field.secondaryValue;
  const hasMixedValues =
    secondaryValue !== undefined && primaryValue !== secondaryValue;

  const displayValue = hasMixedValues
    ? `${formatMarginValue(primaryValue, { fallback: "0px" })}, ${formatMarginValue(secondaryValue, { fallback: "0px" })}`
    : formatMarginValue(primaryValue);

  const handleValueChange = useCallback(
    (newValue: string) => {
      const parsed = parseCommaSeparatedValues(newValue);
      if (parsed && field.onChangeMixed) {
        field.onChangeMixed(
          clampForPadding(parsed.first, spacingType),
          clampForPadding(parsed.second, spacingType),
        );
        return;
      }
      field.onChange(parseSpacingInputValue(newValue, spacingType));
    },
    [field, spacingType],
  );

  const baseAllowedNonUnitValues =
    spacingType === "margin" ? ["auto", "Auto"] : [];
  const allowedNonUnitValues = hasMixedValues
    ? [...baseAllowedNonUnitValues, displayValue]
    : baseAllowedNonUnitValues;

  return (
    <LengthInputSelector
      field={field.key}
      value={displayValue}
      onChange={handleValueChange}
      startEnhancer={field.icon}
      {...getCommonLengthInputProps(spacingType)}
      allowedNonUnitValues={allowedNonUnitValues}
    />
  );
};

function getInitialMode(spacingValue: SpacingValue): SpacingMode {
  const verticalEqual = spacingValue.top === spacingValue.bottom;
  const horizontalEqual = spacingValue.left === spacingValue.right;
  return verticalEqual && horizontalEqual ? "unified" : "individual";
}

const SpacingModifier = <T extends SpacingModifierType>({
  onChange,
  config,
}: SpacingModifierProps<T>) => {
  const stylePrefix = config.type === "margin" ? "margin" : "padding";

  const autoPassthrough = { allowableNonUnitValues: ["auto"] } as const;

  const topValue = useSemanticModifierValue({
    semanticExtractor: ({ computedStyles }) => {
      return parsePixelValue(
        computedStyles[`${stylePrefix}Top` as keyof typeof computedStyles],
        autoPassthrough,
      );
    },
  });

  const rightValue = useSemanticModifierValue({
    semanticExtractor: ({ computedStyles }) => {
      return parsePixelValue(
        computedStyles[`${stylePrefix}Right` as keyof typeof computedStyles],
        autoPassthrough,
      );
    },
  });

  const bottomValue = useSemanticModifierValue({
    semanticExtractor: ({ computedStyles }) => {
      return parsePixelValue(
        computedStyles[`${stylePrefix}Bottom` as keyof typeof computedStyles],
        autoPassthrough,
      );
    },
  });

  const leftValue = useSemanticModifierValue({
    semanticExtractor: ({ computedStyles }) => {
      return parsePixelValue(
        computedStyles[`${stylePrefix}Left` as keyof typeof computedStyles],
        autoPassthrough,
      );
    },
  });

  const extractedValue = useMemo(() => {
    return {
      top: topValue,
      right: rightValue,
      bottom: bottomValue,
      left: leftValue,
    };
  }, [topValue, rightValue, bottomValue, leftValue]);

  const [currentValue, setCurrentValue] = useOverridableState(
    extractedValue,
    undefined,
  );

  const [mode, setMode] = useState<SpacingMode>(() => {
    return getInitialMode(currentValue);
  });

  const createModifiersValues = useCallback(
    (value: SpacingValue) => {
      const modifiers: T[] = [];

      if (value.top !== undefined) {
        modifiers.push({
          type: config.type,
          direction: "top",
          value: value.top,
        } as T);
      }
      if (value.right !== undefined) {
        modifiers.push({
          type: config.type,
          direction: "right",
          value: value.right,
        } as T);
      }
      if (value.bottom !== undefined) {
        modifiers.push({
          type: config.type,
          direction: "bottom",
          value: value.bottom,
        } as T);
      }
      if (value.left !== undefined) {
        modifiers.push({
          type: config.type,
          direction: "left",
          value: value.left,
        } as T);
      }

      return modifiers;
    },
    [config.type],
  );

  const updateSpacingValue = useCallback(
    (updates: Partial<SpacingValue>) => {
      const newValue = { ...currentValue, ...updates };
      setCurrentValue(newValue);
      const modifiers = createModifiersValues(newValue);
      onChange(modifiers);
    },
    [currentValue, setCurrentValue, createModifiersValues, onChange],
  );

  const inputConfigs: Record<SpacingMode, SpacingField[]> = useMemo(
    () => ({
      unified: [
        {
          key: "vertical",
          label: "Vertical",
          icon: <DirectionVertical className="w-4 h-4" />,
          value: currentValue?.top,
          secondaryValue: currentValue?.bottom,
          onChange: (newValue) => {
            updateSpacingValue({
              top: newValue,
              bottom: newValue,
            });
          },
          onChangeMixed: (topVal, bottomVal) => {
            updateSpacingValue({
              top: topVal,
              bottom: bottomVal,
            });
          },
        },
        {
          key: "horizontal",
          label: "Horizontal",
          icon: <DirectionHorizontal className="w-4 h-4" />,
          value: currentValue?.left,
          secondaryValue: currentValue?.right,
          onChange: (newValue) => {
            updateSpacingValue({
              left: newValue,
              right: newValue,
            });
          },
          onChangeMixed: (leftVal, rightVal) => {
            updateSpacingValue({
              left: leftVal,
              right: rightVal,
            });
          },
        },
      ],
      individual: [
        {
          key: "top",
          label: "Top",
          icon: <DirectionTop className="w-4 h-4" />,
          value: currentValue?.top,
          onChange: (newValue) => {
            updateSpacingValue({ top: newValue });
          },
        },
        {
          key: "right",
          label: "Right",
          icon: <DirectionRight className="w-4 h-4" />,
          value: currentValue?.right,
          onChange: (newValue) => {
            updateSpacingValue({ right: newValue });
          },
        },
        {
          key: "bottom",
          label: "Bottom",
          icon: <DirectionBottom className="w-4 h-4" />,
          value: currentValue?.bottom,
          onChange: (newValue) => {
            updateSpacingValue({ bottom: newValue });
          },
        },
        {
          key: "left",
          label: "Left",
          icon: <DirectionLeft className="w-4 h-4" />,
          value: currentValue?.left,
          onChange: (newValue) => {
            updateSpacingValue({ left: newValue });
          },
        },
      ],
    }),
    [currentValue, updateSpacingValue],
  );

  const inputs = inputConfigs[mode];

  const handleModeToggle = () => {
    setMode(mode === "unified" ? "individual" : "unified");
  };

  return (
    <>
      {mode === "unified" ? (
        <div className="grid grid-cols-[1fr_1fr_min-content] gap-2 items-center">
          {inputs.map((input) => (
            <SpacingInput
              key={input.key}
              field={input}
              spacingType={config.type}
            />
          ))}
          <Tooltip content={config.individualTooltipText}>
            <Button
              icon
              variant="secondary"
              size="sm"
              onClick={handleModeToggle}
            >
              <ExpandModifierOptions />
            </Button>
          </Tooltip>
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_1fr_min-content] gap-2 items-center">
          {inputs.slice(0, 2).map((input) => (
            <SpacingInput
              key={input.key}
              field={input}
              spacingType={config.type}
            />
          ))}
          <Tooltip content={config.unifiedTooltipText}>
            <Button
              icon
              variant="secondary"
              size="sm"
              onClick={handleModeToggle}
            >
              <ExpandModifierOptions />
            </Button>
          </Tooltip>
          {inputs.slice(2).map((input) => (
            <SpacingInput
              key={input.key}
              field={input}
              spacingType={config.type}
            />
          ))}
          <div></div>
        </div>
      )}
    </>
  );
};

export default SpacingModifier;
